import React, { useEffect, useMemo, useState } from 'react'
import DataTable from 'react-data-table-component'
import { useAdminStoreActions, useAdminStoreData } from '../../store/adminStore'
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import { useTranslation } from 'react-i18next'
import Icon from '../../components/icon/Icon'
import { toast } from 'react-toastify'
import PackageDetailsModal from './PackageDetailsModal'
import PackageAddModal from './PackageAddModal'

const AdminPackages = () => {
  const { fetchPackages, setPackagesPage, setPackagesSize, setPackagesSort, setPackagesFilters, deletePackage } = useAdminStoreActions()
  const packages = useAdminStoreData.packages()
  const loading = useAdminStoreData.packagesLoading()
  const totalElements = useAdminStoreData.packagesTotalElements()
  const page = useAdminStoreData.packagesPage()
  const size = useAdminStoreData.packagesSize()

  const dummyData = []

  const displayData = packages.length > 0 ? packages : dummyData
  const displayTotal = packages.length > 0 ? totalElements : dummyData.length

  const [filterText, setFilterText] = useState('')
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [selectedPkgId, setSelectedPkgId] = useState(null)
  const [addOpen, setAddOpen] = useState(false)
  const [copyPackageId, setCopyPackageId] = useState(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [packageToDelete, setPackageToDelete] = useState(null)

  const { t } = useTranslation()

  useEffect(() => {
    const timer = setTimeout(() => {
      if (filterText === '' || filterText.length >= 3) {
        setPackagesFilters({ name: filterText })
      }
    }, 1000)

    return () => clearTimeout(timer)
  }, [filterText, setPackagesFilters])

  useEffect(() => {
    fetchPackages()
  }, [])

  const handleViewClick = (row) => {
    setSelectedPkgId(row.id)
    setDetailsOpen(true)
  }

  const handleDeleteClick = (id) => {
    setPackageToDelete(id)
    setDeleteConfirmOpen(true)
  }

  const confirmDelete = async () => {
    if (packageToDelete) {
      try {
        await deletePackage(packageToDelete)
        toast.success(t('admin.table.deleteSuccess', 'Paket başarıyla silindi.'))
      } catch (error) {
        toast.error(error?.detail || t('admin.table.deleteError', 'Paket silinirken bir hata oluştu.'))
      } finally {
        setDeleteConfirmOpen(false)
        setPackageToDelete(null)
      }
    }
  }

  const formatDuration = (row) => {
    const parts = []
    if (row.durationYear) parts.push(`${row.durationYear} ${t('admin.duration.year', 'Yıl')}`)
    if (row.durationMonth) parts.push(`${row.durationMonth} ${t('admin.duration.month', 'Ay')}`)
    if (row.durationDay) parts.push(`${row.durationDay} ${t('admin.duration.day', 'Gün')}`)
    return parts.join(' ') || '-'
  }

  const handleCopyClick = (id) => {
    setCopyPackageId(id)
    setAddOpen(true)
  }

  const columns = useMemo(() => [
    {
      name: t('admin.table.packageName', 'Paket Adı'),
      selector: row => row.name || '-',
      sortable: true,
      sortField: 'name',
      wrap: true
    },
    {
      name: t('admin.table.packageDuration', 'Süre'),
      selector: row => formatDuration(row),
      sortable: false,
      wrap: true
    },
    {
      name: t('admin.table.price', 'Fiyat'),
      selector: row => `${row.price} ${row.currency || 'TRY'}`,
      sortable: true,
      sortField: 'price',
      width: '120px'
    },
    {
      name: t('admin.table.action', 'İşlem'),
      minWidth: '150px',
      cell: row => (
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Button className="modern-action-btn btn-primary" onClick={() => handleViewClick(row)} title={t('admin.table.view', 'Görüntüle')}>
            <Icon name="eye" style={{ fontSize: '1rem' }}/>
          </Button>
          <Button className="modern-action-btn btn-success" onClick={() => handleCopyClick(row.id)} title={t('admin.table.copy', 'Kopyala')}>
            <Icon name="copy" style={{ fontSize: '1rem' }}/>
          </Button>
          <Button className="modern-action-btn btn-danger" onClick={() => handleDeleteClick(row.id)} title={t('admin.table.delete', 'Sil')}>
            <Icon name="trash" style={{ fontSize: '1rem' }}/>
          </Button>
        </div>
      )
    }
  ], [t])

  const handlePageChange = page => {
    setPackagesPage(page - 1)
  }

  const handlePerRowsChange = (newPerPage, page) => {
    setPackagesSize(newPerPage)
  }

  const handleSort = (column, sortDirection) => {
    if (column.sortField) {
      setPackagesSort(`${column.sortField},${sortDirection}`)
    }
  }

  const paginationComponentOptions = {
    rowsPerPageText: t('admin.pagination.rowsPerPage', 'Rows per page:'),
    rangeSeparatorText: t('admin.pagination.rangeSeparator', 'of'),
    selectAllRowsItem: false,
    selectAllRowsItemText: 'All'
  }

  const customStyles = {
    table: {
      style: {
        backgroundColor: '#ffffff'
      }
    },
    headRow: {
      style: {
        backgroundColor: '#f9fafb',
        borderBottomWidth: '1px',
        borderBottomColor: '#e5e7eb',
        borderTopWidth: '1px',
        borderTopColor: '#e5e7eb',
        minHeight: '48px'
      }
    },
    headCells: {
      style: {
        color: '#4b5563',
        fontSize: '0.75rem',
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        paddingLeft: '1.25rem',
        paddingRight: '1.25rem'
      }
    },
    cells: {
      style: {
        color: '#374151',
        fontSize: '0.875rem',
        paddingLeft: '1.25rem',
        paddingRight: '1.25rem',
        borderBottomWidth: '1px',
        borderBottomColor: '#f3f4f6',
        paddingTop: '0.5rem',
        paddingBottom: '0.5rem'
      }
    },
    pagination: {
      style: {
        borderTopWidth: '1px',
        borderTopColor: '#e5e7eb',
        color: '#6b7280',
        fontSize: '0.875rem'
      }
    }
  }

  return (
    <div className="admin-packages">
      <style>
        {`
          .modern-card-container {
            background-color: #ffffff;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
            overflow: hidden;
            margin-bottom: 2rem;
          }
          .modern-toolbar {
            padding: 1.25rem;
            display: flex;
            align-items: center;
            justify-content: flex-start;
            gap: 1rem;
            background-color: #ffffff;
          }
          .modern-input-wrapper {
            position: relative;
            display: flex;
            align-items: center;
          }
          .modern-input {
            border: 1px solid #d1d5db !important;
            border-radius: 6px !important;
            padding: 0.5rem 0.75rem !important;
            font-size: 0.875rem !important;
            background-color: #ffffff !important;
            color: #374151 !important;
            transition: all 0.2s;
            box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
          }
          .modern-search-input {
            width: 250px !important;
          }
          .modern-input:focus {
            border-color: #6366f1 !important;
            box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2) !important;
            outline: none !important;
          }
          .btn-modern-primary {
             background-color: #6366f1 !important;
             border-color: #6366f1 !important;
             border-radius: 6px !important;
             font-weight: 500 !important;
             padding: 0.5rem 1rem !important;
             color: white !important;
             box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05) !important;
             display: inline-flex;
             align-items: center;
             gap: 0.5rem;
             transition: all 0.2s;
          }
          .btn-modern-primary:hover {
             background-color: #4f46e5 !important;
             border-color: #4f46e5 !important;
          }
          .modern-action-btn {
             border-radius: 6px !important;
             padding: 0.25rem 0.5rem !important;
             box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05) !important;
          }
          .modern-action-btn.btn-primary {
             background-color: #6366f1 !important;
             border-color: #6366f1 !important;
          }
          .modern-action-btn.btn-primary:hover { background-color: #4f46e5 !important; }
          
          .modern-action-btn.btn-success {
             background-color: #10b981 !important;
             border-color: #10b981 !important;
          }
          .modern-action-btn.btn-success:hover { background-color: #059669 !important; }

          .modern-action-btn.btn-danger {
             background-color: #ef4444 !important;
             border-color: #ef4444 !important;
          }
          .modern-action-btn.btn-danger:hover { background-color: #dc2626 !important; }
        `}
      </style>
      <div className="modern-card-container">
        <div className="modern-toolbar">
          <Button className="btn-modern-primary" onClick={() => {
            setCopyPackageId(null)
            setAddOpen(true)
          }}>
            <Icon name="plus"/> {t('admin.table.addNew', 'Yeni Ekle')}
          </Button>
          <div className="modern-input-wrapper">
            <input
              type="text"
              className="modern-input modern-search-input"
              placeholder={t('admin.table.searchPackageName', 'Paket Adına Göre Ara')}
              onChange={(e) => setFilterText(e.target.value)}
            />
          </div>
        </div>
        <DataTable
          columns={columns}
          data={displayData}
          progressPending={loading}
          pagination
          paginationServer
          paginationTotalRows={displayTotal}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          paginationDefaultPage={page + 1}
          paginationPerPage={size}
          paginationComponentOptions={paginationComponentOptions}
          sortServer
          onSort={handleSort}
          highlightOnHover
          pointerOnHover
          customStyles={customStyles}
          noDataComponent={<div className="p-4 text-center text-muted">{t('admin.table.noData', 'No records found')}</div>}
        />
      </div>
      <PackageDetailsModal isOpen={detailsOpen} toggle={() => setDetailsOpen(!detailsOpen)} packageId={selectedPkgId}/>
      <PackageAddModal isOpen={addOpen} toggle={() => setAddOpen(!addOpen)} onRefresh={fetchPackages} copyPackageId={copyPackageId}/>

      <Modal isOpen={deleteConfirmOpen} toggle={() => setDeleteConfirmOpen(!deleteConfirmOpen)} className="modal-dialog-centered">
        <ModalHeader toggle={() => setDeleteConfirmOpen(!deleteConfirmOpen)}>{t('admin.table.deleteConfirmTitle', 'Paketi Sil')}</ModalHeader>
        <ModalBody>
          {t('admin.table.deleteConfirm', 'Bu paketi silmek istediğinizden emin misiniz?')}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setDeleteConfirmOpen(false)}>{t('admin.modal.cancel', 'İptal')}</Button>
          <Button color="danger" onClick={confirmDelete}>{t('admin.modal.delete', 'Sil')}</Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default AdminPackages
